/**
 * Attention hint for tray UI elements: signals "an input is expected here now".
 * Two variants — a pulsing ring around the element, or a pulsing glow surface
 * placed behind it. Styles are injected on first use, same pattern as TrayChoice.
 */

export type TrayHintStyle = 'pulseBorder' | 'glowBackdrop';

const GLOW_PADDING_PX = 6;
const FALLBACK_RADIUS = '10px';

const STYLE_CLASSES: Record<TrayHintStyle, string> = {
  pulseBorder: 'tray-hint-border',
  glowBackdrop: 'tray-hint-glow',
};

type ActiveHint = {
  backdrop: HTMLElement | null;
  detach: () => void;
};

const activeHints = new WeakMap<HTMLElement, ActiveHint>();
let areStylesInjected = false;

export function showTrayHint(target: HTMLElement, style: TrayHintStyle) {
  injectStyles();
  hideTrayHint(target);

  // The ring keeps an even gap on every side by growing its inset, so it needs the
  // element's own corner radius to widen along with it.
  target.style.setProperty('--tray-hint-radius', readCornerRadius(target));
  target.classList.add('tray-hint-target', STYLE_CLASSES[style]);

  const needsBackdrop = style === 'glowBackdrop';
  activeHints.set(target, needsBackdrop ? attachGlowBackdrop(target) : createHintWithoutBackdrop());
}

export function hideTrayHint(target: HTMLElement) {
  target.classList.remove('tray-hint-target', STYLE_CLASSES.pulseBorder, STYLE_CLASSES.glowBackdrop);
  target.style.removeProperty('--tray-hint-radius');

  const hint = activeHints.get(target);
  if (!hint) return;
  activeHints.delete(target);
  hint.detach();
  hint.backdrop?.remove();
}

function createHintWithoutBackdrop(): ActiveHint {
  return { backdrop: null, detach: () => {} };
}

/**
 * The glow is a real sibling div behind the target: the tray option buttons have
 * a transparent background, so a pulsing surface underneath stays visible.
 */
function attachGlowBackdrop(target: HTMLElement): ActiveHint {
  const host = target.parentElement;
  if (!host) return createHintWithoutBackdrop();

  host.classList.add('tray-hint-host');
  const backdrop = document.createElement('div');
  backdrop.className = 'tray-hint-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');
  host.insertBefore(backdrop, target);

  resizeBackdropToTarget(backdrop, target);

  // Tray elements stretch with the viewport, so observing the target covers
  // window resizes too — no global listener that would outlive the element.
  const observer = new ResizeObserver(() => resizeBackdropToTarget(backdrop, target));
  observer.observe(target);

  return { backdrop, detach: () => observer.disconnect() };
}

function resizeBackdropToTarget(backdrop: HTMLElement, target: HTMLElement) {
  backdrop.style.left = `${target.offsetLeft - GLOW_PADDING_PX}px`;
  backdrop.style.top = `${target.offsetTop - GLOW_PADDING_PX}px`;
  backdrop.style.width = `${target.offsetWidth + GLOW_PADDING_PX * 2}px`;
  backdrop.style.height = `${target.offsetHeight + GLOW_PADDING_PX * 2}px`;
  backdrop.style.borderRadius = `calc(${readCornerRadius(target)} + ${GLOW_PADDING_PX}px)`;
}

/** Single corner value — the ring radius is derived from it via `calc`. */
function readCornerRadius(target: HTMLElement): string {
  const radius = window.getComputedStyle(target).borderTopLeftRadius;
  if (!radius || radius === '0px') return FALLBACK_RADIUS;
  return radius;
}

function injectStyles() {
  if (areStylesInjected) return;
  areStylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    .tray-hint-host {
      position: relative;
    }
    .tray-hint-target {
      position: relative;
      z-index: 2;
    }
    .tray-hint-border::after {
      content: "";
      position: absolute;
      inset: -4px;
      border: 3px solid var(--primary-color);
      border-radius: calc(var(--tray-hint-radius, 10px) + 4px);
      pointer-events: none;
      animation: tray-hint-border-pulse 1.2s ease-in-out infinite;
    }
    .tray-hint-backdrop {
      position: absolute;
      pointer-events: none;
      z-index: 0;
      background-color: var(--primary-color);
      animation: tray-hint-glow-pulse 1.2s ease-in-out infinite;
    }
    /* Animating inset instead of transform keeps the gap identical on all four
       sides — scaling a wide, flat element widens it far more than it heightens. */
    @keyframes tray-hint-border-pulse {
      0%, 100% {
        inset: -2px;
        border-radius: calc(var(--tray-hint-radius, 10px) + 2px);
        opacity: 0.65;
        box-shadow: 0 0 0 0 var(--primary-color);
      }
      50% {
        inset: -7px;
        border-radius: calc(var(--tray-hint-radius, 10px) + 7px);
        opacity: 1;
        box-shadow: 0 0 14px 0 var(--primary-color);
      }
    }
    @keyframes tray-hint-glow-pulse {
      0%, 100% { opacity: 0.3; box-shadow: 0 0 0 0 var(--primary-color); }
      50% { opacity: 0.75; box-shadow: 0 0 18px 6px var(--primary-color); }
    }
    @media (prefers-reduced-motion: reduce) {
      .tray-hint-border::after {
        animation: none;
        opacity: 1;
      }
      .tray-hint-backdrop {
        animation: none;
        opacity: 0.6;
      }
    }
  `;
  document.head.appendChild(style);
}
